// The bordered "additional information" panel: who ran it, how long it took,
// how it was invoked, and the deep links out to AWS and Observer.

import React from 'react';
import PropTypes from 'prop-types';
import { Trans, t } from '@lingui/macro';
import { Flex, P } from '@decisiv/ui-components';
import ExternalLink from '@decisiv/iconix/lib/components/ExternalLink';

import authorizeOn from '../../utils/resourceAuthorizer';
import { formatLogUrl } from './executionLinks';
import { InfoPanel } from './style';

const Entry = ({ label, children = null, weight = 'semibold' }) => (
  <Flex>
    <P color="charcoal" weight={weight}>
      {label}&nbsp;
    </P>
    <P weight="regular" color="alaskanHusky">
      {children}
    </P>
  </Flex>
);

Entry.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
  weight: PropTypes.string,
};
export default function AdditionalInformation({
  log,
  requestDetailUrl = '',
  isRequestDetailReady = false,
}) {
  const executionName = log?.execution_arn?.split(':')?.pop();

  return (
    <InfoPanel name="header-info">
      <P weight="semibold">
        <Trans>ADDITIONAL INFORMATION</Trans>
      </P>

      <Entry label={<Trans>Executed by:</Trans>}>{log?.created_by}</Entry>

      {log?.response_from_cache && (
        <Entry label={<Trans>Cache Age:</Trans>}>{log?.cache_age}</Entry>
      )}

      <Entry label={<Trans>Duration:</Trans>}>
        {log?.time_taken} {log.time_taken > 1 ? t`seconds` : t`milliseconds`}
      </Entry>

      <Entry label={<Trans>Invocation Type:</Trans>}>
        {log?.trigger_type === 'application_event'
          ? t`Application Event`
          : t`HTTP Api Request`}
      </Entry>

      {/* Both rows always render: which one carries a value is what tells the
          reader how this execution was triggered. */}
      <span>
        <P color="charcoal" weight="default">
          <Trans>Application Event:</Trans>&nbsp;
        </P>
        <P weight="regular" color="alaskanHusky">
          {log?.trigger_type === 'application_event'
            ? log?.action_or_application_event
            : '⁠—'}
        </P>
      </span>
      <span>
        <P color="charcoal" weight="default">
          <Trans>HTTP Api Request:</Trans>&nbsp;
        </P>
        <P weight="regular" color="alaskanHusky">
          {log?.trigger_type === 'http_api_request'
            ? log?.action_or_application_event
            : '⁠—'}
        </P>
      </span>

      <Entry label={<Trans>Invocation Method:</Trans>}>
        {log?.sync ? t`Synchronous` : t`Asynchronous`}
      </Entry>

      <Flex flexDirection="column">
        <Flex>
          <P weight="semibold">
            <Trans>AWS Step Function: </Trans>
          </P>
          {log?.execution_arn && (
            <Flex marginLeft={0.5}>
              {authorizeOn() ? (
                // The KDS Modal body calls preventDefault on every click, so
                // the link must stop propagation to navigate. No window.open:
                // it opened a second tab.
                <a
                  target="_blank"
                  href={formatLogUrl(log)}
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {executionName} <ExternalLink size="medium" />
                </a>
              ) : (
                <span>{executionName} </span>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex flexDirection="column">
        <Flex>
          <P weight="semibold">
            <Trans>Request Detail: </Trans>
          </P>
          {isRequestDetailReady && (
            <Flex marginLeft={0.5}>
              <a
                target="_blank"
                href={requestDetailUrl}
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Trans>View in Observer</Trans> <ExternalLink size="medium" />
              </a>
            </Flex>
          )}
        </Flex>
      </Flex>
    </InfoPanel>
  );
}

AdditionalInformation.propTypes = {
  log: PropTypes.oneOfType([PropTypes.object]).isRequired,
  requestDetailUrl: PropTypes.string,
  isRequestDetailReady: PropTypes.bool,
};
